import type * as models from 'pkg/api/models';
import type { Dateable } from 'pkg/api/models/dateable';
import type { LibraryTag } from 'pkg/api/models/library_tag';
import type { Record } from 'pkg/api/models/record';
import { type User } from 'pkg/api/models/user';

export enum SessionGroupUserRole {
	Member = 'member',
	// Runs the session group. Attribution and visibility only — no
	// permission surface.
	Leader = 'leader',
}

export interface SessionGroupUser extends Record, Dateable {
	sessionGroupId: number;
	userId: number;
	role: SessionGroupUserRole;
	user?: User;
}

export interface SessionGroupFocus extends Record, Dateable {
	sessionGroupId: number;
	libraryTagId: number;
	weight: number;
	libraryTag?: LibraryTag;
}

export interface SessionGroup extends Record, Dateable {
	practicePlanGroupId: number;
	name: string;
	// Lowercase #rrggbb, used to tell the groups apart across the planner.
	color: string;
	sortOrder: number;
	users?: SessionGroupUser[];
	// The focus mix, heaviest weight first; absent while the group has none.
	focus?: SessionGroupFocus[];
}

export interface SessionGroupCreatePayload {
	name: string;
	// Lowercase #rrggbb.
	color: string;
	sortOrder?: number;
}

export interface SessionGroupFocusPayload {
	libraryTagPublicId: string;
	weight: number;
}

export interface SessionGroupUpdatePayload {
	name?: string;
	color?: string;
	sortOrder?: number;
	// The group's whole focus mix: omitted keeps what is stored, an empty list
	// clears it.
	focus?: SessionGroupFocusPayload[];
}

export interface RosterClient {
	addUser(
		sessionGroupId: number,
		userId: number,
		role: SessionGroupUserRole
	): Promise<Response>;
	updateUserRole(
		sessionGroupId: number,
		userId: number,
		role: SessionGroupUserRole
	): Promise<Response>;
	removeUser(sessionGroupId: number, userId: number): Promise<Response>;
}

export interface EventSessionGroups {
	planGroup: models.practicePlan.PracticePlanGroup;
	customized: boolean;
	sessionGroups: SessionGroup[];
	eventUsers: models.eventUser.EventUser[];
}
